import moment from 'moment'
import type { CSSProperties, ReactElement } from 'react'
import { dayBounds } from 'lib/appointmentCalendar'
import { formatMinutes } from 'lib/dayClock'
import { VisitBlock } from './CalendarVisit'
import type { AppointmentsCalendarState } from './useAppointmentsCalendar'

/** Tall enough for an hour's visit to carry its name, time and type. */
const HOUR_PX = 72
const GUTTER_PX = 56
const COLUMN_MIN_PX = 168
/** Room for the name line, however short the visit. */
const MIN_BLOCK_PX = 20

/**
 * One day the way the practice's own schedule shows it: a column per provider, visits to scale.
 * Read only; a visit opens its patient.
 */
export function CalendarDayBoard({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const { columns, visits } = calendar
  const bounds = dayBounds(visits)
  const hours = Array.from({ length: (bounds.end - bounds.start) / 60 }, (_, index) => bounds.start + index * 60)
  const height = ((bounds.end - bounds.start) / 60) * HOUR_PX
  const toPx = (minutes: number) => ((minutes - bounds.start) / 60) * HOUR_PX
  const nowMinutes = moment(calendar.now).diff(moment(calendar.now).startOf(`day`), `minutes`)
  const showNow = calendar.day === calendar.today && nowMinutes >= bounds.start && nowMinutes <= bounds.end

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-default bg-surface">
      <div
        className="grid min-w-(--board-min-width) grid-cols-(--board-columns)"
        style={
          {
            '--board-columns': `${GUTTER_PX}px repeat(${columns.length}, minmax(${COLUMN_MIN_PX}px, 1fr))`,
            '--board-min-width': `${GUTTER_PX + columns.length * COLUMN_MIN_PX}px`,
            '--board-height': `${height}px`,
          } as CSSProperties
        }
      >
        <div className="sticky left-0 top-0 z-30 border-b border-r border-default bg-surface" />
        {columns.map((column) => (
          <div
            key={column.key}
            className="sticky top-0 z-20 min-w-0 border-b border-l border-default bg-surface px-2 py-2"
          >
            <div className="truncate text-body-sm font-semibold text-strong" title={column.title}>
              {column.title}
            </div>
            <div className="truncate text-body-xs text-subtle" title={column.subtitle}>
              {[column.subtitle, `${column.visits.length} ${column.visits.length === 1 ? `visit` : `visits`}`]
                .filter(Boolean)
                .join(` · `)}
            </div>
          </div>
        ))}

        <div className="sticky left-0 z-10 h-(--board-height) border-r border-default bg-surface">
          {hours.map((hour) => (
            <span
              key={hour}
              className="absolute right-2 top-(--at) text-body-xs tabular-nums text-subtle"
              style={{ '--at': `${toPx(hour) + 4}px` } as CSSProperties}
            >
              {formatMinutes(hour).replace(`:00`, ``)}
            </span>
          ))}
        </div>
        {columns.map((column) => (
          <div key={column.key} className="relative h-(--board-height) border-l border-default">
            {hours.slice(1).map((hour) => (
              <div
                key={hour}
                className="absolute inset-x-0 top-(--at) border-t border-subtle"
                style={{ '--at': `${toPx(hour)}px` } as CSSProperties}
              />
            ))}
            {showNow && (
              // oxlint-disable-next-line shadcn/no-raw-colors -- the current-time line is red on every calendar
              <div
                className="absolute inset-x-0 top-(--at) z-5 h-0.5 bg-red-500"
                style={{ '--at': `${toPx(nowMinutes)}px` } as CSSProperties}
              />
            )}
            {column.visits.map((visit) => (
              <VisitBlock
                key={visit.appointment.id}
                visit={visit}
                calendar={calendar}
                style={
                  {
                    '--visit-top': `${toPx(visit.start) + 1}px`,
                    '--visit-height': `${Math.max(toPx(visit.end) - toPx(visit.start) - 2, MIN_BLOCK_PX)}px`,
                    '--visit-left': `calc(${(visit.lane / visit.lanes) * 100}% + 3px)`,
                    '--visit-width': `calc(${100 / visit.lanes}% - 6px)`,
                  } as CSSProperties
                }
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
